import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { FiCode, FiEdit3, FiGlobe, FiSave, FiTrash2, FiX, FiZap } from "react-icons/fi";
import type { ContentDocument, ContentTypeConfig } from "../API/Models";
import { client } from "../Clients";
import { PreviewLinks } from "./Content/ContentListView";
import ContentStatusPill, { deriveContentStatus } from "../Components/ContentStatusPill";
import PublishProductionButton from "../Components/PublishProductionButton";
import UnpublishProductionButton from "../Components/UnpublishProductionButton";
import { LinkedTicketsBanner } from "../Components/Content/LinkedTicketsBanner";
import { EditorAuthorshipBanner } from "../Components/Content/EditorAuthorshipBanner";
import { getMyProfile } from "../Components/Account/accountClient";
import { usePageHeader } from "../hooks/usePageHeader";
import { useDeploymentLinks } from "../hooks/useDeploymentLinks";
import ContentItemForm, { adaptBodyFieldsForSave, validateContentItemValues } from "../Components/ContentItemForm";
import { Modal } from "../Components/Modal";
import { showSuccessModal } from "../Helper/showSuccessModal";
import type { IApplicationState } from "../Redux/Reducers";
import TaxonomyMultiSelect from "../Components/TaxonomyMultiSelect";
import CategoryPathSelect, { buildCategoryPathLabel } from "../Components/CategoryPathSelect";
import FieldInfoTip from "../Components/FieldInfoTip";
import { getFieldUsageNote } from "../Components/fieldUsageNotes";
import AIAnalyzeModal from "../Components/AIAnalyzeModal";
import ContentHistoryPanel from "../Components/ContentHistory/ContentHistoryPanel";
import ContentVersionsPanel from "../Components/ContentVersions/ContentVersionsPanel";
import ContentLinksPanel from "../Components/ContentLinksPanel";
import type { ContentVersion } from "../Components/ContentVersions/contentVersionsClient";
import { useRealtimeEvent } from "../Realtime/RealtimeProvider";
import { EditorPresence } from "../Realtime/EditorPresence";
import { usePermissions } from "../Auth/PermissionsContext";
import { TicketCreateModal } from "../Components/Tickets/TicketCreateModal";
import EditorActionBar from "../Components/Content/EditorActionBar";
import ReachabilityModal from "../Components/Content/ReachabilityModal";
import EditorCommitActions from "../Components/Content/EditorCommitActions";
import { FiInbox } from "react-icons/fi";
import { toast } from "react-toastify";

const DEFAULT_LANGUAGE = "fr";

const normalizeContentTypeKey = (value?: string | null) =>
	(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

const resolveContentTypeConfig = (configs: ContentTypeConfig[], idOrKey?: string | null) => {
	if (!idOrKey) return null;
	const normalized = idOrKey.trim().toLowerCase();
	const byId = configs.find((item) => (item.id ?? "").toLowerCase() === normalized);
	if (byId) return byId;
	return configs.find((item) => normalizeContentTypeKey(item.title ?? "") === normalized) ?? null;
};

const extractValues = (data: any) => {
	if (!data || typeof data !== "object") return {};
	return { ...data };
};

/** Individual content editor with dynamic form, taxonomies, categories, AI analysis and translation. */
const ContentItem: React.FC = () => {
	const { contentTypeId, itemId } = useParams<{ contentTypeId: string; itemId: string }>();
	const history = useHistory();
	const contentTypeConfigs = useSelector((state: IApplicationState) => state.contentTypeConfigs.items);
	const contentTypeConfigsInitialized = useSelector((state: IApplicationState) => state.contentTypeConfigs.initialized);
	const taxonomies = useSelector((state: IApplicationState) => state.taxonomies.items);
	const categories = useSelector((state: IApplicationState) => state.categories.items);
	const decodedContentTypeId = useMemo(() => {
		try {
			return decodeURIComponent(contentTypeId);
		} catch {
			return contentTypeId;
		}
	}, [contentTypeId]);
	const decodedItemId = useMemo(() => {
		try {
			return decodeURIComponent(itemId);
		} catch {
			return itemId;
		}
	}, [itemId]);
	const config = useMemo(
		() => resolveContentTypeConfig(contentTypeConfigs ?? [], decodedContentTypeId),
		[contentTypeConfigs, decodedContentTypeId]
	);
	const schemaKey = useMemo(() => {
		if (config?.id) return config.id;
		if (!contentTypeConfigsInitialized) return null;
		return decodedContentTypeId?.trim() ?? null;
	}, [config?.id, contentTypeConfigsInitialized, decodedContentTypeId]);
	const [draft, setDraft] = useState<ContentDocument | null>(null);
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [formError, setFormError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [editing, setEditing] = useState(false);
	// Set when an earlier version has been loaded into the form (local revert) : drives the amber
	// banner telling the operator to Save to make it current, or Cancel to keep what was live.
	const [loadedVersion, setLoadedVersion] = useState<ContentVersion | null>(null);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [showJson, setShowJson] = useState(false);
	const [showAnalyze, setShowAnalyze] = useState(false);
	// Liens Dev/Prod resolus par id de contenu, re-lus tout seuls a la fin de chaque
	// deploiement (le deploiement preview d'un contenu neuf est asynchrone).
	const { links: deployLinks, refresh: refreshDeployLinks } = useDeploymentLinks(decodedItemId);
	const [publishing, setPublishing] = useState(false);
	// Local account display name, for the author-field « Mon nom » quick-fill (fetched once).
	const [myDisplayName, setMyDisplayName] = useState<string>("");
	const lastLoadKey = useRef<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		getMyProfile()
			.then((p) => !cancelled && setMyDisplayName((p.displayName ?? "").trim()))
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, []);

	const loadItem = useCallback(async () => {
		if (!schemaKey) return;
		setLoading(true);
		setFormError(null);
		try {
			const response = await client.content.apiContentSchemaKeyLanguageGet(
				schemaKey,
				DEFAULT_LANGUAGE,
				decodedItemId
			);
			const current = response.data?.current ?? null;
			setDraft(current);
			setFormValues(extractValues(current?.data));
			setFieldErrors({});
		} catch (err: any) {
			console.error(err);
			setFormError("Unable to load content.");
		} finally {
			setLoading(false);
		}
	}, [decodedItemId, schemaKey]);

	useEffect(() => {
		if (!schemaKey) return;
		const key = `${schemaKey}:${decodedItemId}`;
		if (lastLoadKey.current === key) return;
		lastLoadKey.current = key;
		loadItem();
	}, [decodedItemId, loadItem, schemaKey]);

	// Realtime hot-reload: when ANOTHER admin saves THIS document, refetch its data (not a page reload).
	// We ignore our OWN save echo (isMe) and repeats of the same content hash.
	const { isMe } = usePermissions();
	const [showTicket, setShowTicket] = useState(false);
	const [showReachability, setShowReachability] = useState(false);
	const appliedSaveHashRef = useRef<string | null>(null);
	useRealtimeEvent("content-saved", (evt: any) => {
		const id = draft?.id ?? decodedItemId;
		if (!id || evt?.id !== id) return;
		if (isMe(evt?.user)) return;
		if (evt?.dataHash && evt.dataHash === appliedSaveHashRef.current) return;
		appliedSaveHashRef.current = evt?.dataHash ?? null;
		loadItem();
		const who = evt?.user && evt.user !== "SYSTEM" ? evt.user : "un autre utilisateur";
		toast.info(`Contenu mis à jour par ${who} : données rechargées.`);
	});

	const hasConfig = Boolean(config?.fields?.length);
	const canEdit = Boolean(config);
	const supportsTaxonomy = Boolean(config?.supportsTaxonomy);
	const supportsCategory = Boolean(config?.supportsCategory) && (categories ?? []).length > 0;
	const taxonomyIds = useMemo(() => (formValues?.taxonomyIds ?? []).filter(Boolean), [formValues]);
	const categoryPathIds = useMemo(
		() => (formValues?.categoryPathIds ?? []).filter(Boolean),
		[formValues]
	);
	const taxonomyMap = useMemo(() => {
		const map = new Map<string, string>();
		taxonomies.forEach((taxonomie) => {
			if (taxonomie.id) {
				map.set(taxonomie.id, taxonomie.title ?? taxonomie.id);
			}
		});
		return map;
	}, [taxonomies]);
	const taxonomyLabels = useMemo(
		() => taxonomyIds.map((id) => taxonomyMap.get(id) ?? id),
		[taxonomyIds, taxonomyMap]
	);
	const categoryLabel = useMemo(
		() => buildCategoryPathLabel(categories ?? [], categoryPathIds),
		[categories, categoryPathIds]
	);
	const pathValue = useMemo(() => {
		const draftPath = (draft?.data as any)?.path;
		const formPath = (formValues as any)?.path;
		return (editing ? formPath ?? draftPath : draftPath) ?? "";
	}, [draft?.data, editing, formValues]);
	// Manual path override (data.pathOverride). In edit mode the override is ACTIVE as soon as
	// the key exists in the form values (even empty, while the operator is typing); outside
	// edit mode only a non-empty stored value counts.
	const overrideEditing = editing && (formValues as any)?.pathOverride != null;
	const overrideStored = String((draft?.data as any)?.pathOverride ?? "").trim() !== "";
	const overrideActive = editing ? overrideEditing : overrideStored;
	const handleStartOverride = () =>
		setFormValues((prev) => ({ ...prev, pathOverride: String((prev as any)?.pathOverride ?? pathValue ?? "") }));
	const handleRemoveOverride = () =>
		setFormValues((prev) => {
			const { pathOverride: _removed, ...rest } = prev as any;
			return rest;
		});
	const jsonPayload = useMemo(() => {
		const source = editing ? formValues : draft?.data;
		return JSON.stringify(source ?? {}, null, 2);
	}, [draft?.data, editing, formValues]);

	// Is this content live in production? (marker, or resolved production links as fallback for older
	// content whose marker isn't stamped.) Used to gate delete : you must unpublish first.
	// Un contenu encore déployé en production ne peut pas être supprimé : pour tout le monde,
	// déployeurs compris : il faut d'abord « Dépublier » (le serveur refuse en 409 sinon).
	// EditorCommitActions substitue déjà « Dépublier » à « Supprimer » sur cette base.
	const isProdDeployed = draft?.isProductionDeployed === true || (deployLinks?.production.length ?? 0) > 0;
	// Links shown in the action bar: the read-store urls when known, else the expected public path
	// for the targets the markers say are live (a record without a listing card has no D1 row to
	// resolve from, yet it IS online at its template url). Never the bare record id.
	const expectedPath = deployLinks?.expected?.[0];
	const previewLinkUrls = deployLinks
		? deployLinks.preview.length > 0
			? deployLinks.preview
			: expectedPath && draft?.isPreviewDeployed === true
				? [expectedPath]
				: []
		: undefined;
	const productionLinkUrls = deployLinks
		? deployLinks.production.length > 0
			? deployLinks.production
			: expectedPath && draft?.isProductionDeployed === true
				? [expectedPath]
				: []
		: undefined;
	// Simplified honest state: production marker + timestamps from the document, with the
	// resolved production links as fallback for older content whose marker isn't stamped.
	const publishStatus = deriveContentStatus({
		isProductionDeployed: isProdDeployed,
		updatedAt: draft?.updatedAt ?? null,
		productionDeployedAt: draft?.productionDeployedAt ?? null,
		publishing
	});

	const handleStartEdit = () => {
		setEditing(true);
		// Lazy body migration starts here: a legacy HTML-string body becomes its components
		// shape as soon as the operator enters edit mode, so the save below persists the array.
		setFormValues(adaptBodyFieldsForSave(config?.fields ?? [], extractValues(draft?.data)));
		setFieldErrors({});
		setFormError(null);
	};

	const handleCancelEdit = () => {
		setEditing(false);
		setFormValues(extractValues(draft?.data));
		setFieldErrors({});
		setFormError(null);
		setLoadedVersion(null);
	};

	// Load an earlier version into the form (LOCAL revert): enter edit mode, replace the form values
	// with the snapshot's data (same migration pass as handleStartEdit), and surface the banner. The
	// operator still has to Save to persist it : nothing is written until then.
	const handleLoadVersion = (data: Record<string, any>, version: ContentVersion) => {
		setEditing(true);
		setFormValues(adaptBodyFieldsForSave(config?.fields ?? [], extractValues(data)));
		setFieldErrors({});
		setFormError(null);
		setLoadedVersion(version);
	};

	const handleSave = async () => {
		if (!config || !schemaKey) return;
		// Defense in depth: even if the values never went through handleStartEdit, a
		// string body is migrated to components before it reaches the API.
		const valuesToSave = adaptBodyFieldsForSave(config.fields ?? [], formValues);
		const validation = validateContentItemValues(config.fields ?? [], valuesToSave);
		setFieldErrors(validation.errors);
		if (validation.hasErrors) {
			setFormError("Corrigez les champs signalés.");
			return;
		}
		setFormError(null);
		setSaving(true);
		try {
			const payloadDoc: ContentDocument = {
				contentType: schemaKey,
				language: DEFAULT_LANGUAGE,
				id: draft?.id ?? decodedItemId,
				isDisabled: draft?.isDisabled ?? false,
				isDeleted: false,
				data: valuesToSave
			};
			const response = await client.content.apiContentSchemaKeyLanguagePut(
				schemaKey,
				DEFAULT_LANGUAGE,
				decodedItemId,
				payloadDoc
			);
			const current = response.data?.current ?? payloadDoc;
			setDraft(current);
			setEditing(false);
			setLoadedVersion(null);
			const savedId = current?.id ?? decodedItemId;
			showSuccessModal({
				title: "Content updated",
				message: `${config.title ?? "Content"} has been updated.`,
				// Pour qui detient le droit : enregistrer et mettre en production en un geste.
				publish: savedId
					? {
							contentId: savedId,
							contentType: schemaKey,
							onPublished: () => {
								loadItem();
								refreshDeployLinks();
							}
						}
					: undefined
			});
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? "Save failed.";
			setFormError(String(apiMessage));
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!config || !schemaKey) return;
		setDeleting(true);
		setFormError(null);
		try {
			const payloadDoc: ContentDocument = {
				contentType: schemaKey,
				language: DEFAULT_LANGUAGE,
				id: draft?.id ?? decodedItemId,
				isDisabled: draft?.isDisabled ?? false,
				isDeleted: true,
				data: draft?.data ?? {}
			};
			await client.content.apiContentSchemaKeyLanguagePut(schemaKey, DEFAULT_LANGUAGE, decodedItemId, payloadDoc);
			showSuccessModal({
				title: "Content deleted",
				message: `${config.title ?? "Content"} has been deleted.`
			});
			history.push(`/content/${encodeURIComponent(contentTypeId)}`);
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? "Delete failed.";
			setFormError(String(apiMessage));
		} finally {
			setDeleting(false);
			setShowDeleteConfirm(false);
		}
	};

	const renderBody = () => {
		if (loading) return <p className="text-sm text-slate-600">Loading content…</p>;
		if (!contentTypeConfigsInitialized && !config) return <p className="text-sm text-slate-600">Loading content type…</p>;
		if (!config) return <p className="text-sm text-slate-600">This content type is unavailable.</p>;

		return (
			<div className="space-y-4">
				{formError && (
					<div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
						{formError}
					</div>
				)}
				{(config.supportsDynamicPath || pathValue) && (
					<div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
						<div className="flex items-center justify-between gap-2">
							<p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
								Chemin
								<FieldInfoTip note={getFieldUsageNote("path")} />
								{overrideActive && (
									<span className="ml-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-amber-700">
										Remplacé manuellement
									</span>
								)}
							</p>
							{editing && config.supportsDynamicPath && (
								overrideActive ? (
									<button
										type="button"
										onClick={handleRemoveOverride}
										disabled={saving}
										className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
									>
										Rétablir le chemin automatique
									</button>
								) : (
									<button
										type="button"
										onClick={handleStartOverride}
										disabled={saving}
										className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
									>
										Remplacer le chemin
									</button>
								)
							)}
						</div>
						{editing && overrideActive ? (
							<>
								<input
									type="text"
									value={String((formValues as any)?.pathOverride ?? "")}
									onChange={(e) =>
										setFormValues((prev) => ({ ...prev, pathOverride: e.target.value }))
									}
									disabled={saving}
									placeholder="mon-chemin/personnalise"
									className="w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-mono text-slate-700 focus:border-amber-400 focus:outline-none"
								/>
								<p className="text-xs text-slate-500">
									Appliqué à l'enregistrement : minuscules, sans accents, tirets ; un suffixe est
									ajouté si l'adresse est déjà prise. Laisser vide revient au chemin automatique.
								</p>
							</>
						) : (
							<div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-mono text-slate-700">
								{pathValue || (editing ? "Path will be generated on save." : "No path generated yet.")}
							</div>
						)}
					</div>
				)}
				{loadedVersion && (
					<div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
						<span className="font-semibold">
							Version du {new Date(loadedVersion.createdAt).toLocaleString("fr-FR")} chargée.
						</span>{" "}
						Enregistrez pour la rétablir, ou Annuler pour conserver la version actuelle.
					</div>
				)}
				<ContentItemForm
					fields={config.fields ?? []}
					value={formValues}
					onChange={setFormValues}
					errors={fieldErrors}
					disabled={!editing || saving}
					authorQuickFillName={myDisplayName}
				/>
				{supportsCategory && (
					<div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
						<div className="flex items-center justify-between">
							<p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
								Catégories
								<FieldInfoTip note={getFieldUsageNote("categoryPathIds")} />
							</p>
						</div>
						{editing ? (
							<CategoryPathSelect
								categories={categories}
								value={categoryPathIds}
								onChange={(next) => setFormValues((prev) => ({ ...prev, categoryPathIds: next }))}
								placeholder="Assign category path"
								helper="Choose a single category path for this content."
								disabled={saving}
							/>
						) : (
							<div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
								{categoryLabel ? categoryLabel : "No category assigned."}
							</div>
						)}
					</div>
				)}
				{supportsTaxonomy && (
					<div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
						<div className="flex items-center justify-between">
							<p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
								Taxonomies
								<FieldInfoTip note={getFieldUsageNote("taxonomyIds")} />
							</p>
						</div>
						{editing ? (
							<TaxonomyMultiSelect
								options={taxonomies}
								value={taxonomyIds}
								onChange={(next) => setFormValues((prev) => ({ ...prev, taxonomyIds: next }))}
								placeholder="Assign taxonomies"
								helper="Filter and select taxonomies for this content."
							/>
						) : (
							<div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
								{taxonomyLabels.length ? taxonomyLabels.join(", ") : "No taxonomies assigned."}
							</div>
						)}
					</div>
				)}
				{!hasConfig && <p className="text-sm text-slate-600">No fields are defined for this content type.</p>}
			</div>
		);
	};

	// Declared here, after the handlers it needs. The commit actions live in the app's TOP bar, hard
	// right : the same component and the same corner as the page editor (Pages/PageEditor.tsx).
	usePageHeader({
		title: config?.title ?? "Content",
		actions: (
			<EditorCommitActions
				isEditing={editing}
				onStartEdit={handleStartEdit}
				editDisabled={!canEdit}
				onCancelEdit={handleCancelEdit}
				onSave={handleSave}
				saveDisabled={saving || !canEdit}
				saving={saving}
				onDelete={() => setShowDeleteConfirm(true)}
				deleteDisabled={!canEdit || deleting}
				deleting={deleting}
				contentId={draft?.id ?? decodedItemId}
				publishDisabled={!draft}
				isProductionDeployed={isProdDeployed}
				onPublished={() => {
					loadItem();
					refreshDeployLinks();
				}}
				onPublishingChange={setPublishing}
			/>
		)
	});

	return (
		<>
			{/* The SAME secondary bar as the page editor (Components/Content/EditorActionBar) : minus the
			    page-tree toggle and the Blocs/Markdown/JSON switcher, which only a page has. The commit
			    actions are in the app's top bar (usePageHeader above), exactly as on the page editor, and
			    the title is not repeated here because the top bar already shows it. */}
			<EditorActionBar
				status={publishStatus}
				previewUrls={previewLinkUrls}
				productionUrls={productionLinkUrls}
				isEditing={editing}
				onAnalyze={() => setShowAnalyze(true)}
				analyzeDisabled={!draft}
				translateHref={`/traduction/${encodeURIComponent(schemaKey ?? "")}?slug=${encodeURIComponent(decodedItemId)}&lang=en`}
				onCreateTicket={() => setShowTicket(true)}
				onShowReachability={() => setShowReachability(true)}
				reachabilityDisabled={!decodedItemId}
				onOpenJson={() => setShowJson(true)}
				jsonDisabled={!draft}
			/>
			<ReachabilityModal open={showReachability} contentId={decodedItemId} onClose={() => setShowReachability(false)} />
			<div className="p-6 space-y-6">
			{/* Who else is viewing this content right now (presence by path). */}
			<EditorPresence />
			{/* Open tickets linked to this content, then the last-editor / last-publisher banner. */}
			<LinkedTicketsBanner contentId={draft?.id ?? decodedItemId} />
			<EditorAuthorshipBanner contentId={draft?.id ?? decodedItemId} />
			<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">{renderBody()}</div>
			<ContentLinksPanel
				previewUrls={previewLinkUrls}
				productionUrls={productionLinkUrls}
				fallbackPath={expectedPath ?? null}
			/>
			{/* Stacked full width, same order as the page editor: liens → versions → historique. */}
			<ContentVersionsPanel
				contentId={draft?.id ?? decodedItemId}
				onLoad={handleLoadVersion}
				loadedVersionId={loadedVersion?.id ?? null}
				onReturnToCurrent={() => {
					loadItem();
					setLoadedVersion(null);
				}}
			/>
			<ContentHistoryPanel contentId={draft?.id ?? decodedItemId} />

			<TicketCreateModal
				open={showTicket}
				onClose={() => setShowTicket(false)}
				linkedContentId={draft?.id ?? decodedItemId}
				linkedContentType={schemaKey}
				defaultCategory="contenu"
			/>

			<Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} size="sm" centered>
				<Modal.Header closeButton>
					<Modal.Title>Delete content?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-700">
						Are you sure you want to delete this content? This action cannot be undone.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setShowDeleteConfirm(false)}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={() => handleDelete()}
						className="inline-flex items-center rounded-full border border-rose-200 bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
						disabled={deleting}
					>
						{deleting ? "Deleting…" : "Delete"}
					</button>
				</Modal.Footer>
			</Modal>
			<Modal show={showJson} onHide={() => setShowJson(false)} size="lg" centered>
				<Modal.Header closeButton>
					<Modal.Title>Content JSON</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<pre className="max-h-[65vh] overflow-auto rounded-lg bg-slate-900 p-4 text-xs text-slate-100">
						{jsonPayload}
					</pre>
				</Modal.Body>
			</Modal>
			<AIAnalyzeModal
				show={showAnalyze}
				onClose={() => setShowAnalyze(false)}
				schemaKey={schemaKey ?? ""}
				language={DEFAULT_LANGUAGE}
				contentId={draft?.id ?? decodedItemId}
				data={draft?.data ?? {}}
				analyzableFields={
					config?.fields
						?.filter((f: any) => f.analyzeMistakes || f.analyzeSeo)
						.map((f: any) => f.key)
						.filter(Boolean) ?? undefined
				}
				cachedResult={(draft as any)?.analysisResult ?? null}
				cachedHash={(draft as any)?.analysisHash ?? null}
				onResultCached={async (resultJson, hash) => {
					try {
						// Save analysis result back to the content document
						const payloadDoc = {
							contentType: schemaKey,
							language: DEFAULT_LANGUAGE,
							id: draft?.id ?? decodedItemId,
							isDisabled: draft?.isDisabled ?? false,
							isDeleted: false,
							data: { ...(draft?.data ?? {}), analysisResult: resultJson, analysisHash: hash }
						};
						await client.content.apiContentSchemaKeyLanguagePut(
							schemaKey ?? "", DEFAULT_LANGUAGE, decodedItemId, payloadDoc as any
						);
					} catch (err) {
						console.warn("Failed to persist analysis result:", err);
					}
				}}
			/>
			</div>
		</>
	);
};

export default ContentItem;
